import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ExchangeRateDto,
  ResponseDtoExchangeRateDto,
  ResponseDtoPageExchangeRateDto,
  ResponseDtoExchangeRateMetadataDto,
  ExchangeRateRequestDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

// Interface for exchange rate filters
export interface ExchangeRateFilters {
  baseCurrencies?: string[];
  toCurrencies?: string[];
  exchangeRates?: string[];
  timeBases?: string[];
  times?: string[];
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// Interface for pagination and search parameters
interface ExchangeRateListParams {
  page?: number;
  size?: number;
  sortingColum?: string;
  sortingDirection?: 'ASC' | 'DESC';
}

/**
 * Hook to fetch a paginated list of exchange rates
 * @param params Pagination and sorting parameters
 * @returns Query result with paginated exchange rates data
 */
export const useExchangeRateList = (params: ExchangeRateListParams, filters?: ExchangeRateFilters): UseQueryResult<ResponseDtoPageExchangeRateDto> =>
  useQuery(
    ['exchangeRates', params, filters],
    async () => {
      // Build request data with filters and pagination
      const requestDto: ExchangeRateRequestDto = {
        paginationRequest: {
          page: params.page,
          size: params.size,
          sortingColum: params.sortingColum, // Using the correct parameter name
          sortingDirection: params.sortingDirection,
        },
      };

      // Add filters if they exist
      if (filters) {
        if (filters.baseCurrencies?.length) requestDto.baseCurrencies = filters.baseCurrencies;
        if (filters.toCurrencies?.length) requestDto.toCurrencies = filters.toCurrencies;
        if (filters.exchangeRates?.length) requestDto.exchangeRates = filters.exchangeRates;
        if (filters.timeBases?.length) requestDto.timeBases = filters.timeBases;
        if (filters.times?.length) requestDto.times = filters.times;
      }

      const response = await tFinancialService.api.getExchangeRates(requestDto);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

/**
 * Hook to create or update exchange rates
 * @returns Mutation function for upserting exchange rates
 */
export const useUpsertExchangeRate = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (exchangeRate: ExchangeRateDto): Promise<ResponseDtoExchangeRateDto> => {
      const response = await tFinancialService.api.saveOrUpdateExchangeRate(exchangeRate);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['exchangeRates']);
      },
    }
  );
};

export const useDeleteExchangeRate = () => {
  const queryClient = useQueryClient();

  return useMutation(async ({ id }: { id: string }) => tFinancialService.api.deleteExchangeRate(id), {
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries(['exchangeRates'])]);
    },
  });
};

/**
 * Hook to fetch exchange rates metadata for filtering options
 * This provides filter options for currencies, rates, time bases, etc.
 */
export const useExchangeRatesMetadata = (filters: ExchangeRateFilters = {}): UseQueryResult<ResponseDtoExchangeRateMetadataDto> =>
  useQuery(
    ['exchangeRatesMetadata', filters],
    async (): Promise<ResponseDtoExchangeRateMetadataDto> => {
      // Build request data with filters
      const requestDto: ExchangeRateRequestDto = {
        // Default empty pagination request as it's required by the API
        paginationRequest: {},
      };

      // Add pagination if provided
      if (filters.page !== undefined || filters.size !== undefined || filters.sortBy || filters.sortDirection) {
        const paginationRequest: any = {};

        if (filters.page !== undefined) paginationRequest.page = filters.page;
        if (filters.size !== undefined) paginationRequest.size = filters.size;
        if (filters.sortBy) paginationRequest.sortingColum = filters.sortBy;
        if (filters.sortDirection) paginationRequest.sortingDirection = filters.sortDirection;

        requestDto.paginationRequest = paginationRequest;
      }

      // Only add filter arrays if they have values
      if (filters.baseCurrencies?.length) requestDto.baseCurrencies = filters.baseCurrencies;
      if (filters.toCurrencies?.length) requestDto.toCurrencies = filters.toCurrencies;
      if (filters.exchangeRates?.length) requestDto.exchangeRates = filters.exchangeRates;
      if (filters.timeBases?.length) requestDto.timeBases = filters.timeBases;
      if (filters.times?.length) requestDto.times = filters.times;

      return (await tFinancialService.api.getExchangeRatesMetadata(requestDto)).data;
    },
    {
      // Keep the data fresh but don't refetch on window focus
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
